import { Dropdown } from '@fluentui/react';
import { ClassNamesFromIStyles, useClassNames } from '@h2oai/ui-kit';
import { useState } from 'react';

import BarChart from '../../BarChart/BarChart';
import { useAiUnitUsageByPod } from '../AIUnitWorkspacePage.services';
import {
  granularityDropdownStyles,
  IPodUsageContentStyles,
  podUsageContentStylesDefault,
} from './PodUsageContent.styles';

interface IPodUsageContentProps {
  startDate: Date;
  endDate: Date;
  username: string;
  podName: string;
  namespace: string;
  workspace: string;
  onClose: () => void;
}

const PodUsageContent = ({ startDate, endDate, username, podName, namespace, workspace }: IPodUsageContentProps) => {
  const [selectedGranularity, setSelectedGranularity] = useState<'weekly' | 'monthly'>('weekly');

  const { podUsageData, podUsageDataLoading, podUsageDataError } = useAiUnitUsageByPod(
    startDate,
    endDate,
    username,
    podName,
    namespace,
    workspace,
    selectedGranularity
  );

  const classNames = useClassNames<IPodUsageContentStyles, ClassNamesFromIStyles<IPodUsageContentStyles>>(
    'pod-usage-content',
    podUsageContentStylesDefault
  );

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        onClick: (_: any, legendItem: any, legend: any) => {
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);

          meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
          chart.update();
        },
      },
      title: {
        display: false,
        text: `AI Unit Usage`,
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
    },
  };

  const processChartData = (data: any) => {
    if (!data) return data;

    return {
      ...data,
      datasets: data.datasets.map((dataset: any) => ({
        ...dataset,
        hidden: dataset.label === 'Total Usage in Namespace' ? true : false,
      })),
    };
  };

  return (
    <div className={classNames.root}>
      <p className={classNames.paramLabel}>
        Pod Name: <span className={classNames.inlineCode}>{podName}</span>
      </p>
      <p className={classNames.paramLabel}>
        Workspace: <span className={classNames.inlineCode}>{workspace}</span>
      </p>
      <p className={classNames.paramLabel}>
        Namespace: <span className={classNames.inlineCode}>{namespace}</span>
      </p>
      <p className={classNames.paramLabel}>
        Username: <span className={classNames.inlineCode}>{username}</span>
      </p>

      <div className={classNames.timePeriodContainer}>
        <div className={classNames.timePeriod}>
          <p className={classNames.paramLabel}>Time Period: </p>
          {`${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`}
        </div>
        <Dropdown
          label="Granularity"
          options={[
            { key: 'weekly', text: 'Weekly' },
            { key: 'monthly', text: 'Monthly' },
          ]}
          selectedKey={selectedGranularity}
          onChange={(_, option) => {
            if (option) {
              setSelectedGranularity(option.key as 'weekly' | 'monthly');
            }
          }}
          disabled={podUsageDataLoading || !!podUsageDataError}
          styles={granularityDropdownStyles}
        />
      </div>
      <div className={classNames.chartContainer}>
        <BarChart
          data={processChartData(podUsageData)}
          options={options}
          loading={podUsageDataLoading}
          error={!!podUsageDataError}
        />
      </div>
    </div>
  );
};

export default PodUsageContent;
